/*
Copyright (c) 2025, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { memo, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ToolkitTrackerProps } from '../../analytics';
import TButton from '../button';
import { RecommendationListProps } from './types';
import { debounce, generateID } from '../../base/helper';
import './styles.scss';

const RecommendationListContainer = styled.div<{
  leftOverflow: boolean;
  rightOverflow: boolean;
}>`
  -webkit-mask-image: ${({ leftOverflow, rightOverflow }) => `
    linear-gradient(
      to right,
      ${leftOverflow ? 'rgba(0, 0, 0, 0) 0px,' : 'black 0px,'}
      black 50px,
      black ${rightOverflow ? 'calc(100% - 50px)' : '100%'}
      ${rightOverflow ? ', rgba(0, 0, 0, 0) 100%' : ''}
    )
  `};

  mask-image: ${({ leftOverflow, rightOverflow }) => `
    linear-gradient(
      to right,
      ${leftOverflow ? 'rgba(0, 0, 0, 0) 0px,' : 'black 0px,'}
      black 50px,
      black ${rightOverflow ? 'calc(100% - 50px)' : '100%'}
      ${rightOverflow ? ', rgba(0, 0, 0, 0) 100%' : ''}
    )
  `};
`;

const RecommendationList = ({
  id = generateID('clientos-ui-toolkit-recommendation-list'),
  items
}: RecommendationListProps) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [visibility, setVisibility] = useState<boolean[]>([]);
  const [leftOverflow, setLeftOverflow] = useState<boolean[]>([]);
  const [rightOverflow, setRightOverflow] = useState<boolean[]>([]);

  const checkVisibility = () => {
    if (!listRef.current) return;

    const parentRect = listRef.current.getBoundingClientRect();
    const newVisibility: boolean[] = [];
    const newLeftOverflow: boolean[] = [];
    const newRightOverflow: boolean[] = [];

    listRef.current.childNodes.forEach((child, index) => {
      const itemRect = (child as HTMLElement).getBoundingClientRect();
      const leftVisibility = itemRect.left >= parentRect.left;
      if (!leftVisibility) newLeftOverflow[index] = true;
      const rightVisibility = itemRect.right <= parentRect.right;
      if (!rightVisibility) newRightOverflow[index] = true;
      newVisibility[index] = leftVisibility && rightVisibility;
    });

    setVisibility(newVisibility);
    setLeftOverflow(newLeftOverflow);
    setRightOverflow(newRightOverflow);
  };

  useLayoutEffect(() => {
    const debouncedCheckVisibility = debounce(checkVisibility, 300);

    checkVisibility();
    window.addEventListener('resize', debouncedCheckVisibility);
    listRef.current?.addEventListener('scroll', debouncedCheckVisibility);

    return () => {
      window.removeEventListener('resize', debouncedCheckVisibility);
      listRef.current?.removeEventListener('scroll', debouncedCheckVisibility);
    };
  }, [items]);

  return (
    <div
      className="clientos-ui-toolkit-recommendation-list__container"
      id={id}
    >
      <RecommendationListContainer
        id={`${id}__container`}
        ref={listRef}
        className="clientos-ui-toolkit-recommendation-list__buttons"
        leftOverflow={leftOverflow.length > 0}
        rightOverflow={rightOverflow.length > 0}
      >
        {items.map((item, index) => {
          const toolkitTrackerProps = item as ToolkitTrackerProps;
          return (
            <div
              key={index}
              id={`${id}__item-${index}`}
              className={`clientos-ui-toolkit-recommendation-list__item ${visibility[index] ? '' : 'clientos-ui-toolkit-recommendation-list__item--faded'}`}
            >
              <TButton
                {...toolkitTrackerProps}
                {...item}
              />
            </div>
          );
        })}
      </RecommendationListContainer>
    </div>
  );
};

export default memo(RecommendationList);
